import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fallbackReviews = [
  { name: 'David & Rachel K.', location: 'Toronto, Canada', text: 'We flew in from Toronto for a Vegas vacation and this Hollywood day trip was the absolute highlight. Our guide was incredibly knowledgeable and Santa Monica Beach took our breath away.', rating: 5 },
  { name: 'Michelle P.', location: 'Dallas, Texas', text: 'Booked this for my mom\'s birthday and it was PERFECT. She cried happy tears at the Walk of Fame. The hotel pickup made everything effortless.', rating: 5 },
  { name: 'The Anderson Family', location: 'Vancouver, BC', text: 'Traveling with three kids and this tour was a lifesaver. The sit-down lunch at a real restaurant was a game changer for the little ones.', rating: 5 },
  { name: 'James & Li W.', location: 'Calgary, Alberta', text: 'Best value for money tour we have ever done. Breakfast AND lunch included plus all the iconic sights? We could not believe how much was packed into one day.', rating: 5 },
  { name: 'Crystal M.', location: 'Chicago, Illinois', text: 'My husband was at a convention and I did not want to spend another day at the hotel pool. This tour was the BEST decision of our entire Vegas trip!', rating: 5 },
  { name: 'Robert & Anne S.', location: 'Miami, Florida', text: 'We have done the Hollywood tour twice now and both times were incredible. The Rodeo Drive stop is our personal favorite. Five stars across the board!', rating: 5 },
];

const Reviews = () => {
  const [reviews, setReviews] = useState(fallbackReviews);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    axios.get(`${API}/reviews`)
      .then(res => { if (res.data?.length) setReviews(res.data); })
      .catch(() => {});
  }, []);

  const next = () => setCurrent((current + 1) % reviews.length);
  const prev = () => setCurrent((current - 1 + reviews.length) % reviews.length);

  return (
    <section id="reviews" className="section-padding bg-[#FDFBF7]" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <p className="font-caveat text-xl sm:text-2xl text-[#FF007A] mb-2">Real Travelers, Real Stories</p>
          <h2 className="font-bebas text-4xl sm:text-5xl lg:text-6xl tracking-tighter uppercase">
            What Our Guests Are Saying
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
          {reviews.slice(0, 3).map((review, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="card-brutal p-6"
              data-testid={`review-card-${i}`}
            >
              <Quote className="w-8 h-8 text-[#FF007A]/30 mb-3" />
              <div className="flex gap-0.5 mb-3">
                {Array.from({ length: review.rating }).map((_, j) => (
                  <Star key={j} className="w-4 h-4 text-[#FFD600] fill-[#FFD600]" />
                ))}
              </div>
              <p className="font-outfit text-sm sm:text-base text-[#4A4A4A] leading-relaxed mb-4">
                "{review.text}"
              </p>
              <div className="border-t border-gray-200 pt-3">
                <p className="font-outfit font-bold text-sm">{review.name}</p>
                <p className="font-outfit text-xs text-[#4A4A4A]">{review.location}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="card-brutal p-6 sm:p-8 max-w-3xl mx-auto"
          data-testid="review-carousel"
        >
          <div className="flex items-center justify-between mb-4">
            <button onClick={prev} className="w-10 h-10 rounded-full border-2 border-black flex items-center justify-center hover:bg-gray-100 transition-colors" data-testid="review-prev">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex gap-0.5">
              {Array.from({ length: 5 }).map((_, j) => (
                <Star key={j} className="w-5 h-5 text-[#FFD600] fill-[#FFD600]" />
              ))}
            </div>
            <button onClick={next} className="w-10 h-10 rounded-full border-2 border-black flex items-center justify-center hover:bg-gray-100 transition-colors" data-testid="review-next">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
          <blockquote className="font-outfit text-base sm:text-lg text-center text-[#0A0A0A] leading-relaxed mb-4">
            "{reviews[current]?.text}"
          </blockquote>
          <p className="font-outfit font-bold text-center text-sm">{reviews[current]?.name}</p>
          <p className="font-outfit text-xs text-center text-[#4A4A4A]">{reviews[current]?.location}</p>
          <div className="flex justify-center gap-1.5 mt-4">
            {reviews.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                className={`w-2 h-2 rounded-full transition-colors ${i === current ? 'bg-[#FF007A]' : 'bg-gray-300'}`}
                data-testid={`review-dot-${i}`}
              />
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Reviews;
